import { SectionCard, formatDate } from '@datahub/design-system'

export interface CoreProfileSnapshot {
  slug: string
  name: string
  description: string | null
  entity: string | null
  legalStatus: string | null
  companyStage: string | null
  status: string | null
  ownershipStatus: string | null
  type: string | null
  foundingYear: number | null
  incorporationYear: number | null
  websiteUrl: string | null
  address: string | null
  employeeCount: string | null
  keyProductServices: string | null
  parentCompanyNames: string | null
  subsidiaryCompanyNames: string | null
  sectors: string[]
  subsectors: string[]
  countriesOfIncorporation: string[]
  countriesOfOperation: string[]
  markets: string[]
  tags: string[]
  esgTags: string[]
  aiTags: string[]
  revenueBusinessModels: string[]
  customerBusinessModels: string[]
  distributionModels: string[]
  formerNames: { name: string; nameType: string | null }[]
  listings: { exchange: string; effectiveDate: string | null }[]
}

function Rows({ entries }: { entries: [string, string][] }) {
  return (
    <div className="flex flex-col">
      {entries.map(([label, value]) => (
        <div key={label} className="flex items-start" style={{ padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
          <span className="font-mulish text-white flex-shrink-0" style={{ fontSize: 12, width: '42%' }}>{label}</span>
          <span className="font-mulish text-white" style={{ fontSize: 12, flex: 1, wordBreak: 'break-word' }}>{value}</span>
        </div>
      ))}
    </div>
  )
}

/**
 * The handful of things Stears Core holds that the Datahub dictionary has no field for —
 * Core's own tags and markets, its finer sub-sector vocabulary, listings.
 *
 * Everything else Core knows is not shown here: it seeds the company profile above and
 * appears in the same fields the company will edit, marked "Stears" until the company
 * answers. Core is a seed for that one profile, not a second record beside it, and
 * showing the same values twice invited the reader to treat them as two sources.
 */
export function StearsCoreCard({
  profile,
  fetchedAt,
  mappedSubSectors,
}: {
  profile: CoreProfileSnapshot
  fetchedAt: string | null
  /** Sub-sectors that did make it into the profile, so only the leftovers are listed. */
  mappedSubSectors: string[]
}) {
  const mapped = new Set(mappedSubSectors.map((s) => s.toLowerCase()))
  const extraSubSectors = profile.subsectors.filter((s) => !mapped.has(s.toLowerCase()))

  const rows: [string, string | null][] = [
    ['Entity type', profile.entity],
    ['Company stage', profile.companyStage],
    ['Sub-sectors with no Datahub field', extraSubSectors.length > 0 ? extraSubSectors.join(', ') : null],
    ['Markets', profile.markets.length > 0 ? profile.markets.join(', ') : null],
    ['Tags', profile.tags.length > 0 ? profile.tags.join(', ') : null],
    ['Key products and services', profile.keyProductServices],
    ['Subsidiaries named on Stears', profile.subsidiaryCompanyNames],
    [
      'Listings',
      profile.listings.length > 0
        ? profile.listings.map((l) => (l.effectiveDate ? `${l.exchange} from ${l.effectiveDate}` : l.exchange)).join(', ')
        : null,
    ],
  ]
  const entries = rows.filter((r): r is [string, string] => Boolean(r[1]))
  if (entries.length === 0) return null

  return (
    <SectionCard title="Also on Stears, with no Datahub field">
      <div className="flex flex-col">
        <p className="font-mulish text-text-support" style={{ fontSize: 10, lineHeight: '15px', padding: '8px 20px', borderBottom: '0.5px solid #4B5563' }}>
          Context only{fetchedAt ? `, pulled ${formatDate(fetchedAt)}` : ''}. The rest of what Stears holds seeds the
          profile above and is shown there, in the fields the company edits.
        </p>
        <Rows entries={entries} />
      </div>
    </SectionCard>
  )
}
